import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { FileInfo, MenuOptionValue, Paginated, PaginationQuery, Version, Versions } from "../../types";
import { buildUrl, useApi } from "../api";
import { CustomError } from "../errors";
import { CarPlanning, CarPlanningVersion, UpsertCarPlanning } from "./types";

export const useCarPlanningsApi = () => {
  const api = useApi();

  const uploadCsv = useCallback(
    async (data: FormData) => {
      const url = "/admin/car_plannings/import";
      const response = await api.postNoResult<FormData>(url, data);
      api.mutateCache([url, `/admin/car_plannings`]);
      return response;
    },
    [api],
  );

  const exportFile = useCallback(
    (query: PaginationQuery, signal: AbortSignal, isCsv: boolean) => {
      const url = buildUrl("/admin/car_plannings", query);
      const h = new Headers();
      h.append("Accept", isCsv ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      const response = api.get<FileInfo>({ url, signal, headers: h });
      return response;
    },
    [api],
  );

  const reloadLines = useCallback(async () => {
    const url = `/admin/car_plannings/reload`;
    await api.postNoResult(url);
    api.mutateCache([url, `/admin/car_plannings`]);
  }, [api]);

  const getAdminCarPlanning = useCallback(
    (carPlanningId: string, signal: AbortSignal) => {
      const url = `/admin/car_plannings/${carPlanningId}`;
      return useSWRMutation<CarPlanning | null, CustomError, string>(url, (url) =>
        api.get<CarPlanning>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminCarPlannings = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/car_plannings", query);
      return useSWR<Paginated<CarPlanning> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<CarPlanning>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminCarPlanning = useCallback(
    async (body: UpsertCarPlanning): Promise<{ id: string }> => {
      const url = `/admin/car_plannings`;
      const id = await api.post<UpsertCarPlanning, { id: string }>(url, body);
      api.mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminCarPlanning = useCallback(
    async (id: string, body: UpsertCarPlanning) => {
      const url = `/admin/car_plannings/${id}`;
      await api.put<UpsertCarPlanning>(url, body);
      api.mutateCache([url, `/admin/car_plannings`]);
    },
    [api],
  );

  const deleteAdminCarPlanning = useCallback(
    async (carPlanning: CarPlanning): Promise<void> => {
      const url = `/admin/car_plannings/${carPlanning.id}`;
      await api.del(url).then(() => {
        api.mutateCache([`/admin/car_plannings`]);
      });
    },
    [api],
  );

  const getAdminCarPlanningHistory = useCallback(
    (query: PaginationQuery, carPlanningID: string, signal: AbortSignal) => {
      const url = buildUrl(`/admin/car_plannings/${carPlanningID}/versions`, query);
      return useSWRImmutable<Paginated<Version> | null, CustomError, string>(url, (url) =>
        api.get<Paginated<Version>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminCarPlanningVersion = useCallback(
    (carPlanningId: string, versionNumber: number, signal: AbortSignal) => {
      const url = `/admin/car_plannings/${carPlanningId}/versions/${versionNumber}`;
      return useSWRImmutable<Versions<CarPlanningVersion> | null, CustomError, string>(url, (url) =>
        api.get<Versions<CarPlanningVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const getFieldFilters = useCallback(
    (fieldName: string, signal: AbortSignal) => {
      const url = `/admin/car_plannings/filters?field=${fieldName}`;
      return useSWRMutation<MenuOptionValue[] | null, CustomError, string>(url, (url) =>
        api.get<MenuOptionValue[]>({ url, signal }),
      );
    },
    [api],
  );

  return {
    deleteAdminCarPlanning,
    updateAdminCarPlanning,
    insertAdminCarPlanning,
    useAdminCarPlannings,
    getAdminCarPlanning,
    getAdminCarPlanningHistory,
    getAdminCarPlanningVersion,
    getFieldFilters,
    uploadCsv,
    reloadLines,
    exportFile,
  };
};
